import React, { useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import ExplanationBox from '../shared/ExplanationBox';
import GuardrailWarning from '../shared/GuardrailWarning';
import Tooltip from '../shared/Tooltip';
import { calculateDefaults } from '../../utils/decisionLogic';

const Step1B_Paradigm = () => {
  const { data, updateData, warnings } = useWizard();

  const recommendedParadigm = data.recommendedParadigm;
  const dimensions = data.philosophicalDimensions || {};

  useEffect(() => {
    if (!data.philosophy && recommendedParadigm) {
      updateData('philosophy', recommendedParadigm);
    } else if (!data.philosophy) {
      const defaults = calculateDefaults(data.intake);
      if (defaults.philosophy) {
        updateData('philosophy', defaults.philosophy);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paradigms = [
    {
      value: 'positivism',
      label: 'Positivism / Postpositivism',
      creswell: 'Postpositivist Worldview',
      description: 'Reality is objective and can be observed and measured. Knowledge comes from empirical evidence and hypothesis testing.',
      bestFor: 'Experiments, surveys, statistical testing, cause-effect relationships',
      alignment: {
        ontology: 'realist',
        epistemology: 'objectivist',
        axiology: 'value_free'
      }
    },
    {
      value: 'interpretivism',
      label: 'Interpretivism / Constructivism',
      creswell: 'Constructivist Worldview',
      description: 'Reality is subjectively constructed through human interpretation. Knowledge comes from understanding meanings and contexts.',
      bestFor: 'Interviews, case studies, ethnography, understanding lived experiences',
      alignment: {
        ontology: 'relativist',
        epistemology: 'subjectivist',
        axiology: 'value_laden'
      }
    },
    {
      value: 'pragmatism',
      label: 'Pragmatism',
      creswell: 'Pragmatic Worldview',
      description: 'Focus on "what works" to solve practical problems. Reality is both objective and subjective. Use any method that helps answer the question.',
      bestFor: 'Mixed methods, action research, applied problem-solving',
      alignment: {
        ontology: 'pragmatic',
        epistemology: 'practical',
        axiology: 'pragmatic_values'
      }
    },
    {
      value: 'critical_realism',
      label: 'Critical Realism',
      creswell: 'Postpositivist/Pragmatic (hybrid)',
      description: 'Reality exists independently but our knowledge of it is mediated by perceptions. Seeks to identify underlying mechanisms.',
      bestFor: 'Complex social phenomena, multi-level analysis, explanatory research',
      alignment: {
        ontology: 'realist',
        epistemology: 'critical',
        axiology: 'value_laden'
      }
    },
    {
      value: 'transformative',
      label: 'Transformative / Critical',
      creswell: 'Transformative Worldview',
      description: 'Research should address power inequalities and advocate for marginalized groups. Focus on social justice and change.',
      bestFor: 'Participatory action research, advocacy, emancipatory studies',
      alignment: {
        ontology: 'relativist',
        epistemology: 'critical',
        axiology: 'value_driven'
      }
    }
  ];

  const getAlignmentScore = (paradigm) => {
    if (!dimensions.ontology || !dimensions.epistemology || !dimensions.axiology) {
      return { score: 0, matches: 0, total: 3 };
    }

    let matches = 0;
    const alignment = paradigm.alignment;

    if (alignment.ontology === dimensions.ontology) matches++;
    if (alignment.epistemology === dimensions.epistemology) matches++;
    if (alignment.axiology === dimensions.axiology) matches++;

    return { score: matches, matches, total: 3 };
  };

  const getAlignmentLabel = (score) => {
    if (score === 3) return { text: 'Strong Alignment', class: 'alignment-strong' };
    if (score === 2) return { text: 'Moderate Alignment', class: 'alignment-moderate' };
    if (score === 1) return { text: 'Partial Alignment', class: 'alignment-partial' };
    return { text: 'Low Alignment', class: 'alignment-low' };
  };

  const dimensionLabels = {
    ontology: {
      realist: 'Realism',
      relativist: 'Relativism',
      pragmatic: 'Pragmatic'
    },
    epistemology: {
      objectivist: 'Objectivism',
      subjectivist: 'Subjectivism',
      critical: 'Critical',
      practical: 'Practical'
    },
    axiology: {
      value_free: 'Value-Free',
      value_laden: 'Value-Laden',
      value_driven: 'Value-Driven',
      pragmatic_values: 'Pragmatic'
    }
  };

  const hasConflict = () => {
    if (!data.philosophy || !dimensions.ontology) return false;
    const selectedParadigm = paradigms.find(p => p.value === data.philosophy);
    if (!selectedParadigm) return false;
    const { score } = getAlignmentScore(selectedParadigm);
    return score < 2;
  };

  return (
    <div className="wizard-step">
      <h2 className="step-title">Step 3: Research Paradigm</h2>
      <p className="step-description">
        Based on your philosophical dimensions, select the research paradigm that will guide your study.
        <Tooltip text="A research paradigm is a worldview or framework that shapes your approach to research. It influences your choice of methods, analysis, and how you interpret findings." />
      </p>

      {dimensions.ontology && dimensions.epistemology && dimensions.axiology && (
        <ExplanationBox title="Your Philosophical Position">
          <p>Based on your selections in Step 1A:</p>
          <ul>
            <li><strong>Ontology:</strong> {dimensionLabels.ontology[dimensions.ontology] || dimensions.ontology}</li>
            <li><strong>Epistemology:</strong> {dimensionLabels.epistemology[dimensions.epistemology] || dimensions.epistemology}</li>
            <li><strong>Axiology:</strong> {dimensionLabels.axiology[dimensions.axiology] || dimensions.axiology}</li>
          </ul>
          {recommendedParadigm && (
            <p style={{ marginTop: '0.75rem' }}>
              <strong>Recommended paradigm:</strong>{' '}
              <span className="recommended-highlight">
                {paradigms.find(p => p.value === recommendedParadigm)?.label || recommendedParadigm}
              </span>
            </p>
          )}
        </ExplanationBox>
      )}

      <div className="form-group">
        <label className="form-label">Select your research paradigm:</label>
        <div className="option-cards philosophy-cards">
          {paradigms.map((paradigm, index) => {
            const alignmentInfo = getAlignmentScore(paradigm);
            const alignmentLabel = getAlignmentLabel(alignmentInfo.score);
            const isRecommended = paradigm.value === recommendedParadigm;

            return (
              <div
                key={paradigm.value}
                className={`option-card ${data.philosophy === paradigm.value ? 'selected' : ''} ${index < paradigms.length - 1 ? 'with-separator' : ''} ${isRecommended ? 'recommended' : ''}`}
                onClick={() => updateData('philosophy', paradigm.value)}
              >
                <div className="option-header">
                  <input
                    type="radio"
                    name="philosophy"
                    value={paradigm.value}
                    checked={data.philosophy === paradigm.value}
                    onChange={(e) => updateData('philosophy', e.target.value)}
                  />
                  <span className="option-title">
                    <strong>{paradigm.label}</strong>
                    {isRecommended && <span className="recommended-badge">Recommended</span>}
                  </span>
                </div>

                {dimensions.ontology && (
                  <div className={`alignment-indicator ${alignmentLabel.class}`}>
                    {alignmentLabel.text} ({alignmentInfo.matches}/{alignmentInfo.total} dimensions match)
                  </div>
                )}

                <p className="option-description" style={{ marginBottom: '0.75rem' }}>
                  <strong>Description:</strong> {paradigm.description}
                </p>
                <p className="option-description" style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                  <strong>Best for:</strong> {paradigm.bestFor}
                </p>
                <p className="option-description creswell-mapping">
                  <strong>Creswell mapping:</strong> {paradigm.creswell}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {hasConflict() && (
        <div className="warning-box">
          <div className="warning-title">⚠️ Potential Misalignment</div>
          <div className="warning-content">
            <p>
              The paradigm you selected has low alignment with your philosophical dimensions from Step 1A.
              This may create internal inconsistencies in your research design.
            </p>
            <p style={{ marginTop: '0.5rem' }}>
              <strong>Options:</strong>
            </p>
            <ul>
              <li>Go back to Step 1A and reconsider your philosophical positions</li>
              <li>Choose a paradigm with stronger alignment</li>
              <li>Proceed with justification for why this paradigm works despite the apparent mismatch</li>
            </ul>
          </div>
        </div>
      )}

      <div className="info-box">
        <div className="info-title">Understanding the Mapping</div>
        <div className="info-content">
          <p><strong>Saunders' Research Onion</strong> uses the term "research philosophy" to describe your assumptions about knowledge.</p>
          <p style={{ marginTop: '0.5rem' }}>
            <strong>Creswell's framework</strong> uses "worldview" for similar concepts. We map between them to help you understand both perspectives.
          </p>
        </div>
      </div>

      {warnings.length > 0 && warnings.map((warning, idx) => (
        <GuardrailWarning key={idx} type={warning.type} title={warning.type === 'error' ? 'Required' : 'Note'}>
          {warning.message}
        </GuardrailWarning>
      ))}

      <NavigationButtons
        canProceed={!!data.philosophy}
        proceedMessage="Please select a research paradigm before proceeding."
      />
    </div>
  );
};

export default Step1B_Paradigm;
